import { useState } from 'react';
import { Box, Container, Typography, Stack, IconButton, Avatar } from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import { useLanguage } from '../contexts/LanguageContext';

const SectionContainer = styled(Box)(({ theme }) => ({
  padding: '120px 0',
  background: theme.palette.mode === 'dark'
    ? 'linear-gradient(180deg, #1E293B 0%, #0F172A 100%)'
    : 'linear-gradient(180deg, #F9FAFB 0%, #FFFFFF 100%)',
}));

const TestimonialCard = styled(Box)(({ theme }) => ({
  padding: '48px',
  borderRadius: '32px',
  background: theme.palette.mode === 'dark'
    ? 'rgba(30, 41, 59, 0.6)'
    : 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(20px)',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
  position: 'relative',
  minHeight: '300px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
}));

const QuoteIcon = styled(FormatQuoteIcon)(({ theme }) => ({
  fontSize: '4rem',
  color: theme.palette.primary.main,
  opacity: 0.2,
  position: 'absolute',
  top: 24,
  left: 24,
}));

const NavigationButton = styled(IconButton)(({ theme }) => ({
  background: theme.palette.mode === 'dark'
    ? 'rgba(37, 99, 235, 0.2)'
    : 'rgba(37, 99, 235, 0.1)',
  color: theme.palette.primary.main,
  border: `2px solid ${theme.palette.primary.main}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    background: theme.palette.primary.main,
    color: '#FFFFFF',
    transform: 'scale(1.1)',
  },
  '&:disabled': {
    opacity: 0.3,
  },
}));

export default function TestimonialsSection() {
  const { t, language } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);

  const testimonials = [
    {
      quote: language === 'tr'
        ? 'Teknolobi ile çalışmak harika bir deneyimdi. Projemizi zamanında ve beklentilerimizin üzerinde teslim ettiler. Profesyonel yaklaşımları ve teknik uzmanlıkları etkileyiciydi.'
        : 'Working with Teknolobi was a great experience. They delivered our project on time and exceeded our expectations. Their professional approach and technical expertise were impressive.',
      author: 'Ahmet Yılmaz',
      position: language === 'tr' ? 'CEO, TechStart' : 'CEO, TechStart',
      avatar: 'https://i.pravatar.cc/150?img=12',
    },
    {
      quote: language === 'tr'
        ? 'Dijital dönüşüm sürecimizde Teknolobi\'nin danışmanlığı çok değerliydi. İş süreçlerimizi optimize ettik ve verimliliğimizi %40 artırdık.'
        : 'Teknolobi\'s consulting was very valuable in our digital transformation process. We optimized our business processes and increased our efficiency by 40%.',
      author: 'Zeynep Kaya',
      position: language === 'tr' ? 'CTO, InnovateCorp' : 'CTO, InnovateCorp',
      avatar: 'https://i.pravatar.cc/150?img=5',
    },
    {
      quote: language === 'tr'
        ? 'Özel yazılım çözümleri konusunda gerçekten uzmanlar. İhtiyaçlarımızı tam olarak anladılar ve mükemmel bir ürün geliştirdiler.'
        : 'They are real experts in custom software solutions. They fully understood our needs and developed a perfect product.',
      author: 'Mehmet Demir',
      position: language === 'tr' ? 'Genel Müdür, RetailPlus' : 'General Manager, RetailPlus',
      avatar: 'https://i.pravatar.cc/150?img=8',
    },
  ];

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1));
  };

  const currentTestimonial = testimonials[currentIndex];

  return (
    <SectionContainer component="section" id="projects" aria-labelledby="testimonials-heading">
      <Container maxWidth="lg">
        <Stack spacing={2} alignItems="center" textAlign="center" sx={{ mb: 8 }}>
          <Typography
            id="testimonials-heading"
            variant="h2"
            component="h2"
            sx={{
              fontWeight: 800,
              background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            {t('testimonials.title')}
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ maxWidth: 600 }}
          >
            {t('testimonials.subtitle')}
          </Typography>
        </Stack>

        <Box sx={{ maxWidth: 900, mx: 'auto' }}>
          <TestimonialCard>
            <QuoteIcon />
            
            <Box sx={{ position: 'relative', zIndex: 1, mt: 4 }}>
              <Typography
                variant="h5"
                sx={{
                  fontStyle: 'italic',
                  lineHeight: 1.8,
                  mb: 4,
                  color: 'text.primary',
                }}
              >
                "{currentTestimonial.quote}"
              </Typography>

              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  src={currentTestimonial.avatar}
                  alt={currentTestimonial.author}
                  sx={{ width: 64, height: 64 }}
                />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {currentTestimonial.author}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {currentTestimonial.position}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </TestimonialCard>

          <Stack
            direction="row"
            spacing={2}
            justifyContent="center"
            alignItems="center"
            sx={{ mt: 4 }}
          >
            <NavigationButton onClick={handlePrevious}>
              <ArrowBackIcon />
            </NavigationButton>

            <Stack direction="row" spacing={1}>
              {testimonials.map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    background: (theme) =>
                      index === currentIndex
                        ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`
                        : theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.2)'
                        : 'rgba(0, 0, 0, 0.2)',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                  }}
                  onClick={() => setCurrentIndex(index)}
                />
              ))}
            </Stack>

            <NavigationButton onClick={handleNext}>
              <ArrowForwardIcon />
            </NavigationButton>
          </Stack>
        </Box>
      </Container>
    </SectionContainer>
  );
}